// @cca/safe — ONE escaper per output sink (security.md SEC-3), consumed
// estate-wide so the safe path is a shared helper and the raw path is
// machine-distinguishable (the SEC-1 criteria lint keys on call-wrapped
// interpolation). Pure functions, no bindings; the ONLY platform API is
// WebCrypto (`crypto.subtle`, used by {@link bearerTokenMatches}), which every
// consumer runtime provides — workers, Pages Functions, browsers and the
// Node ≥ 20 test runner — so the same module still serves them all.
//
// History this consolidates (2026-07-15, bead cca-dci): three drifted Zoho
// criteria escapers (forms escaped `():`, parishes `():`, corpus `(),\` — no
// two agreed), four HTML escapers and three CSV escapers. Each sink now has
// exactly one implementation; @cca/conformance fails a new local copy.
//
// The JPEG sink (./jpeg.ts, bead cca-9f0kn.1, ADR-073) lives in its own module
// and is re-exported here, so consumers still import from `@cca/safe` alone.

export {
  JPEG_MAX_SEGMENTS,
  jpegDimensions,
  looksLikeJpeg,
  sanitiseJpeg,
  type JpegLimits,
  type JpegRefusal,
  type JpegResult,
} from './jpeg';

/**
 * Escape a value interpolated into a Zoho CRM **search-criteria term**, i.e.
 * the value slot of `(Field:operator:value)`. Backslash-escapes the union of
 * every structural character the three prior escapers covered: `(` `)` `:`
 * `,` and `\` itself (backslash first — order matters). An unescaped one of
 * these lets a user-supplied value close its term and inject extra
 * `and`/`or` clauses — the 2026-07-14 forms HIGH vuln class.
 */
export function escapeZohoCriteria(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/([():,])/g, '\\$1');
}

/**
 * Escape a value interpolated inside a **single-quoted COQL string literal**
 * (`SELECT … WHERE Email = '<here>'`). COQL follows standard SQL: the single
 * quote is the ONLY in-literal metacharacter and it is escaped by DOUBLING it
 * (`'` → `''`), NOT with a backslash. Live-verified against Zoho CRM
 * (2026-07-17): `WHERE x = 'O''Brien'` parses, while `WHERE x = 'O\'Brien'`
 * throws SYNTAX_ERROR — the backslash is not an escape, so the literal
 * terminates at the lone quote (SEC-1 injection / a broken query on any
 * apostrophe value). Backslash is deliberately left untouched: COQL treats it
 * as an ordinary character, so escaping it would corrupt values that contain a
 * literal `\`.
 */
export function escapeCoqlString(value: string): string {
  return value.replace(/'/g, "''");
}

/**
 * Escape a value interpolated inside a **single-quoted Google Drive query**
 * (`name = '<here>'`, the Drive `files.list` `q` parameter). UNLIKE COQL, Drive
 * query DOES use backslash escaping: `\` and `'` are each prefixed with a
 * backslash (per Google's Drive API query docs). Escape backslash FIRST so an
 * input `\` doesn't double-escape the following quote's backslash.
 */
export function escapeDriveQuery(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * Escape a value interpolated into a Xero API `where` filter's **double-quoted
 * string literal** (`Name=="<here>"`, `EmailAddress=="<here>"`). Xero escapes an
 * embedded double quote by DOUBLING it (`"` → `""`), so a form-supplied name or
 * email can no longer close its literal and inject extra `&&`/`||` filter
 * clauses. Replaces the lossy strip-only (`replace(/"/g, '')`) interpolation in
 * the reimbursement contact lookup: a value with no quote passes through
 * unchanged, so the query stays byte-identical for real names/emails, but a
 * quote is now escaped (kept) rather than deleted. The sibling bank-transaction
 * query validates a GUID shape instead — the right tool for an id, not free text.
 */
export function escapeXeroWhereString(value: string): string {
  return value.replace(/"/g, '""');
}

/**
 * Escape a value interpolated inside a **single-quoted OData string literal**
 * (`$filter=Field eq '<here>'` — the NZ Charities Register feed). OData escapes
 * an embedded single quote by DOUBLING it (`'` → `''`) and treats backslash as
 * an ordinary character, per the OData ABNF for `SQUOTE-in-string`. An unescaped
 * quote lets a value close its literal and append extra `and`/`or` predicates.
 *
 * Byte-identical to {@link escapeCoqlString} today, and deliberately NOT an
 * alias of it: SEC-3 homes one escaper per SINK, and OData is a different sink
 * that only coincidentally shares the rule — sharing the COQL helper would
 * couple an OData filter to a future COQL-only fix and leave a `…Coql…`-named
 * guard on a query that is not COQL. Same call the estate already made for
 * {@link escapeXeroWhereString}, whose doubling coincides with nothing else.
 * Backslash is left untouched for the same reason as COQL: escaping a character
 * the sink treats as ordinary would corrupt values containing a literal `\`.
 */
export function escapeODataString(value: string): string {
  return value.replace(/'/g, "''");
}

/** U+0000 — a byte SQLite's tokenizer stops at, even inside a quoted token. */
const NUL = '\u0000';

/**
 * Escape and quote a SQLite **identifier** (a table or column name in a D1
 * statement) as a double-quoted delimited identifier: an embedded `"` is
 * DOUBLED (`"` → `""`) and the whole name is wrapped in `"…"`, so the result
 * INCLUDES its quotes. Quoting is also what keeps a name that collides with a
 * keyword (`order`) or holds a space parseable. SQLite is this module's fourth
 * quote-doubling sink (SEC-3, bead cca-9962), homed here for the D1 → R2
 * backup WRITER in `@cca/db-backup`, which used to double the quote inline.
 *
 * A name containing U+0000 THROWS a `RangeError` rather than being written:
 * SQLite's tokenizer stops at a NUL even inside a quoted token, no schema name
 * holds one, and an identifier has no escape for it — so such a name means the
 * input is not a schema name at all. The message never repeats the name.
 *
 * Byte-identical in its `"` rule to {@link escapeXeroWhereString}, and
 * deliberately NOT an alias of it: one escaper per SINK, the call recorded on
 * {@link escapeODataString}.
 *
 * IT HAS A TWIN, and the twin is why the parameter is `unknown` (coerced with
 * `String()`) rather than `string`. Root `scripts/` cannot import this package
 * (they are zero-dependency, and its `main` is TypeScript source), so the D1
 * RESTORE tooling carries the same function in `scripts/lib/sqlite-sql.mjs`.
 * `@cca/conformance` (security.test.ts) fails the suite if the two bodies ever
 * differ by more than whitespace. Change both together, never one.
 *
 * WHAT IT DOES NOT DO: make a caller-chosen name safe to use. It quotes an
 * identifier; it does not decide which table a query may touch. Every caller
 * passes a name read from `sqlite_master`. A VALUE belongs in a D1 bound
 * parameter (SEC-1), which is why this module has no SQLite string-literal
 * escaper.
 */
export function escapeSqliteIdentifier(id: unknown): string {
  const name = String(id);
  if (name.includes(NUL)) {
    throw new RangeError('escapeSqliteIdentifier: a SQLite identifier cannot contain U+0000');
  }
  return `"${name.replace(/"/g, '""')}"`;
}

/**
 * Thrown by {@link zohoId} — a "record id" that isn't one. Takes `unknown`
 * because the guard now rejects wrong TYPES as well as wrong shapes: a string
 * or number is quoted into the message (as before), anything else is reported
 * by its `typeof` alone, so a hostile `toString`/`Symbol` can't throw from
 * inside the error path we are on our way to reporting.
 */
export class InvalidZohoIdError extends Error {
  constructor(value: unknown) {
    const shown =
      typeof value === 'string' || typeof value === 'number'
        ? JSON.stringify(String(value).slice(0, 40))
        : `<${typeof value}>`;
    super(`not a Zoho record id: ${shown}`);
    this.name = 'InvalidZohoIdError';
  }
}

/**
 * Assert a value is a Zoho record id (all-digit, as the AU DC issues them)
 * and return it. Use for ids interpolated into criteria/COQL instead of an
 * escaper — an id that needs escaping is not an id, so this fails loudly
 * rather than papering over a tainted value.
 *
 * TYPE first, then shape (2026-07-30, bead cca-sbm2): a declared parameter type
 * is only a compile-time promise, and every real caller feeds this `JSON.parse`
 * output, where TypeScript guarantees nothing. `String(value)` alone COERCES, so
 * an unexpected shape was silently accepted rather than rejected —
 * `String([['113762000001487025']])` flattens to the bare id and the regex then
 * approves it (found by the invite-send probe suite, which pinned it as a
 * documented gap). A number must also be a SAFE integer: beyond 2^53 the
 * coercion returns a *different* id (`9007199254740993` → `…992`) or exponent
 * notation, and `NaN`/`Infinity` have no business reaching the regex at all.
 *
 * The parameter is `unknown` (2026-07-30, Gate-2 S3) so the SIGNATURE matches
 * the argument above: it stayed `string | number` when the type check first
 * landed, which forced this module's own tests to cast `as never` to reach the
 * new branch — a guard whose tests must lie to the compiler is mis-typed, and a
 * narrow declaration is precisely what makes the next reader delete the runtime
 * check as redundant. Do NOT narrow it back; `string | number` is assignable to
 * `unknown`, so the widening changed no call site (all six verified). The return
 * type stays `string` — callers still get a validated id, not an `unknown`.
 */
export function zohoId(value: unknown): string {
  const t = typeof value;
  if (t !== 'string' && t !== 'number') throw new InvalidZohoIdError(value);
  if (t === 'number' && !Number.isSafeInteger(value)) throw new InvalidZohoIdError(value);
  const s = String(value);
  if (!/^\d{1,25}$/.test(s)) throw new InvalidZohoIdError(s);
  return s;
}

/** Thrown by {@link zohoDateTimeCriteria} — a "datetime" that isn't one. */
export class InvalidZohoDateTimeError extends Error {
  constructor(value: string) {
    super(`not an ISO-8601 offset datetime: ${JSON.stringify(value.slice(0, 40))}`);
    this.name = 'InvalidZohoDateTimeError';
  }
}

/**
 * Assert a value is a canonical ISO-8601 offset datetime
 * (`2026-05-22T03:00:56+00:00` — the form Zoho's REST search API accepts) and
 * return it UNCHANGED. This is validation-as-escaping for the **datetime slot**
 * of a criteria term, the same doctrine as {@link zohoId}: datetime values
 * legitimately contain colons, and {@link escapeZohoCriteria} backslashes
 * colons, which CORRUPTS them — Zoho answers INVALID_QUERY
 * (`expected_data_type: datetime, reason: invalid value for search`;
 * live-verified 2026-07-21 when the result-PDF sweep's candidate-pool read
 * failed on every run, while the raw `2026-05-22T03:00:56+00:00` succeeded).
 * A datetime that needs escaping is not a datetime, so anything else —
 * date-only, trailing-`Z`, a backslashed value, injection-shaped input —
 * throws loudly. For system-generated timestamps; this validator IS the sink
 * guard for the slot, so never pass user input hoping it escapes.
 */
export function zohoDateTimeCriteria(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}$/.test(value)) {
    throw new InvalidZohoDateTimeError(value);
  }
  return value;
}

/**
 * Escape a value for interpolation into HTML text or a double-quoted HTML
 * attribute: `& < > " '`. The one HTML escaper — email templates, PDF
 * renderers and editors all consume this.
 *
 * WHAT IT DOES NOT COVER, stated because callers keep having to re-derive it:
 * it is UNSAFE for an UNQUOTED attribute — no backtick, `=` or whitespace is
 * escaped, so `<a href=VALUE>` can still be broken out of — and it performs NO
 * URL-SCHEME VALIDATION, so a `javascript:` (or `data:`) value passes through
 * untouched and remains dangerous in an `href`/`src`. Quote every attribute,
 * and validate the scheme separately before putting a value in a URL slot.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Escape a value interpolated into an iCalendar **TEXT property value**
 * (RFC 5545 §3.3.11 — SUMMARY, LOCATION, DESCRIPTION, …). Backslash first
 * (the escape character), then `;` `,` and LF: an unescaped `;`/`,` splits
 * the property into parameter/list parts, and a raw newline starts a new
 * content line — property injection into the `.ics`. Moved verbatim from the
 * events `.ics` endpoint (2026-07-17, cca-ll0) so the ICS sink has one
 * escaper too; behaviour deliberately unchanged, including that a bare CR
 * passes through (content lines are CRLF-delimited — LF is the live risk).
 */
export function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Encode one CSV cell (RFC 4180 quoting) with the SEC-2 formula-injection
 * guard: a leading `=` `+` `-` `@` tab or CR makes Excel/Sheets evaluate the
 * cell, so such values are prefixed with an apostrophe to force text — but a
 * genuine (optionally signed) number stays numeric so figures still parse.
 * The number carve-out is a FULL match, not a prefix: the retired xero-web
 * copy exempted anything starting `+digit`, which let `+2+5+cmd|…` through
 * as a live formula (found by the @cca/test-probes suite, 2026-07-15).
 */
export function csvCell(v: string | number): string {
  let s = String(v);
  if (/^[=+\-@\t\r]/.test(s) && !/^[+-]?\d+(\.\d+)?$/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** What {@link redactEmailForLog} emits whenever it cannot produce a SAFE mask —
 *  the one fail-closed answer, never the input. */
const REDACTED = '(redacted)';

/** Longest domain kept in a mask; the whole output is then bounded to ~70 chars
 *  (1 initial + `***@` + 60) so one bad value can't dominate a log line. */
const MAX_DOMAIN_LENGTH = 60;

/**
 * The allowlist for the EMITTED INITIAL — ASCII alphanumeric, nothing else.
 * Anchored `^…$`, tested against a single UTF-16 code unit (`local.slice(0, 1)`).
 * Empty input fails this test, so a missing initial can never read as an allowed
 * one. See {@link redactEmailForLog}.
 *
 * **Correction (2026-07-29, cca-158h) — do not believe the earlier version of
 * this comment.** It justified the domain side's NEGATED class by asserting that
 * JS `$` "also matches BEFORE a trailing newline". IT DOES NOT. That is
 * Python/PCRE `$`; in JavaScript, absent the `m` flag, `$` matches ONLY at end of
 * input — measured `false` for a trailing LF and for CRLF, and pinned in
 * `index.test.ts` so the fact cannot rot back. The anchored form
 * `/^[A-Za-z0-9.-]+$/` would therefore have been safe on the domain side too,
 * doubly so because `.trim()` removes a trailing newline before that predicate
 * ever runs. The negated class stays for two smaller and TRUE reasons: it reads
 * as the contract itself (no character outside the set, ANYWHERE in the value),
 * and it depends on nothing upstream — no anchor semantics, no flag, and not on
 * the `.trim()` above remaining in place. A preference for clarity and
 * independence, then; not a hazard avoided. Anchor a regex here if it is the
 * clearer shape — just do not cite that retracted claim as the reason not to.
 */
const SAFE_INITIAL = /^[A-Za-z0-9]$/;

/**
 * Mask an email for a LOG LINE — the one redactor every sign-in door uses. The
 * log sink's escaper, in the same doctrine as the rest of this module: one
 * audited implementation, fail-closed, never a local copy.
 *
 * Each clause and WHY it exists:
 *
 * - **Non-string, or empty once trimmed → `'(redacted)'`.** The parameter is
 *   `unknown` deliberately: a door reads `email` off a JSON body, so `null`, a
 *   number and an object all reach it. Coercing and logging them tells an
 *   operator nothing and can print a whole object.
 * - **EVERY C0 control character and DEL (`\x00`–`\x1F`, `\x7F`) is stripped
 *   BEFORE anything else** — which subsumes `\r` `\n` `\t`. These doors are
 *   PRE-AUTH: anyone on the internet chooses this value. A newline inside it
 *   forges a second log line (and a tab forges a column in a TSV-shaped sink),
 *   so the attacker writes fabricated entries into a production log. The rest of
 *   the class is no less live, because it survives the DOORS' validation: a
 *   caller's `EMAIL_RE` is `[^\s@]`-shaped and JS `\s` covers no C0 control, so
 *   `user@exa\x1b[2Jmple.com` is email-SHAPED, passes validation, and reaches
 *   here — and before this widening the domain slice carried that LIVE ANSI
 *   sequence into the mask, clearing the screen of an operator reading
 *   `wrangler tail`. Stripping first also means no later clause can be fooled by
 *   a control character. **DO NOT DELETE THIS CLAUSE AS "NOW REDUNDANT" — and do
 *   not believe EITHER earlier version of this paragraph: the first claimed the
 *   strip was the last line of defence for the local initial; the second, an
 *   over-correction, claimed its remaining job is FIDELITY ONLY. Both are
 *   wrong.** On the first: since the TWO character allowlists below it is not
 *   that last line of defence — every byte this function emits is either an
 *   allowlisted domain character or an allowlisted initial, so no control
 *   character reaches a log line whether the strip runs or not. On the second —
 *   and this part is LOAD-BEARING FOR REDACTION, not fidelity: `local.length >= 3`
 *   is measured on `cleaned`, i.e. AFTER the strip. Delete the strip and control
 *   characters PAD a short local part past that gate, so the mask emits the real
 *   first character the 1–2-char rule exists to withhold — `a\x00b@example.com`
 *   masks to `***@example.com` today and to `a***@example.com` with the strip
 *   removed. Neither allowlist can catch that: `a` is a perfectly allowed
 *   initial. (Tyson, 2026-07-29, cca-rthl.) The strip ALSO stops a control
 *   character COSTING the log line — with it, `\x1buser@example.com` still masks
 *   to `u***@example.com` and `user@exa\x7fmple.com` still yields the
 *   operationally useful domain; without it the first loses its initial
 *   (`***@example.com`, since `\x1b` is not an allowlisted initial and `.trim()`
 *   would not have removed it — no C0 control except `\t\n\v\f\r` is whitespace)
 *   and the second fails closed to `'(redacted)'`. All three shapes are pinned,
 *   so a strip-deleted mutant reds on a LEAK as well as on fidelity. It is also
 *   the belt to the allowlists' braces should either ever be widened, and the
 *   only clause guaranteed to run before anything parses the value.
 * - **Split on the LAST `@`; no `@`, or an empty domain side → `'(redacted)'`.
 *   NEVER return the input.** THIS IS THE FIX. The three local `defaultRedact`
 *   copies this replaces were `email.replace(/(.).*(@.*)/, '$1***$2')`, and
 *   `String.prototype.replace` returns its input UNCHANGED when the pattern
 *   does not match — so any value without an `@` was logged VERBATIM. That was
 *   reachable in production: `handlePasskeyVerify` in
 *   packages/cca-webauthn/src/handlers.ts accepted ANY non-empty string as
 *   `email` — non-empty plus a credential id, no email shape — and logged it on
 *   the verify-failed path. That door now VALIDATES the shape first (same
 *   `invalid_email`/400 as its sibling `handlePasskeyOptions`), so a value with
 *   no `@` no longer arrives here from THAT caller. This clause is not thereby
 *   redundant, and must not be relaxed on that argument: the doors' validation
 *   admits every control character (above), the authenticated manage routes mask
 *   whatever a portal's `currentUser` returns — which may legitimately be a
 *   non-address account id — and the doctrine is ONE fail-closed implementation,
 *   not a per-caller argument about reachability. Splitting on the LAST `@` (not
 *   the first) is what keeps a local part that itself contains an `@` from
 *   widening the mask.
 * - **The domain side must be `[A-Za-z0-9.-]` THROUGHOUT, or `'(redacted)'`.**
 *   Stripping the control class removes the NEWLINE from
 *   `user@example.com\n2026-07-29 INFO signed in as someone-else`, so no second
 *   log line can be forged — but the forged TEXT survives, lands on the domain
 *   side, and rides out inside the 60-char mask. The contract this function
 *   already states is that a value which is not an ADDRESS is never echoed back
 *   (that is why no-`@` and empty-domain fail closed), and
 *   `example.com2026-07-29 INFO signed in as someone-else` is not an address
 *   either. Without this clause the promise would read "no `@` → refuse, but 45
 *   characters of attacker prose hanging off a real domain → print it", which is
 *   not a contract, only an accident of where the strip landed. Three properties
 *   hold it in place:
 *   1. It is a CHARACTER ALLOWLIST, **not** a domain grammar — no label rules,
 *      no dot placement, no length rules — so `a@mail.corp.example.co.nz` and
 *      every legitimate sub-domained ASCII address pass through untouched. It is
 *      written as a NEGATED class (`/[^…]/` → refuse) rather than an anchored
 *      `/^[…]+$/` for clarity and for depending on nothing upstream; both forms
 *      are safe here, and the claim that the anchored one was NOT is retracted —
 *      see {@link SAFE_INITIAL}.
 *   2. **Validate the FULL domain side, then truncate — and the MECHANISM that
 *      holds that order is a SINGLE BINDING, not this paragraph.** Validating
 *      the truncated slice instead would let 60 clean characters launder
 *      whatever followed them; the truncation exists only to bound the emitted
 *      line. What actually enforces it: `domain` is bound ONCE, from the whole
 *      `cleaned.slice(at + 1)`, and the truncation produces a SEPARATE `shown`.
 *      Merging the two slices into one binding — `const domain = cleaned
 *      .slice(at + 1).slice(0, MAX_DOMAIN_LENGTH)`, an entirely plausible
 *      tidy-up — re-creates the laundering with no other line changing, no type
 *      error, and nothing in the diff that reads as a security change. So the
 *      invariant to preserve is: **the value that is VALIDATED and the value
 *      that is EMITTED are never the same binding.**
 *   3. Two exclusions are KNOWN and DELIBERATE: a raw-unicode IDN
 *      (`user@münchen.de`) and an RFC-legal address literal
 *      (`user@[192.0.2.1]`) now return `'(redacted)'`. That is the fail-closed
 *      answer; the cost is one lossy log line, and both are pinned by tests so
 *      a later reader does not "fix" them.
 * - **Local part ≥ 3 chars → `x***@domain`; 1–2 chars → `***@domain`.** With a
 *   one- or two-character local part the "initial" IS effectively the whole
 *   local part, so showing it is not a redaction. (A zero-length local part —
 *   `@example.org` — takes the same branch for the same reason: there is
 *   nothing to hint at.)
 * - **The emitted INITIAL must be `[A-Za-z0-9]`, or NO initial is shown.** The
 *   same allowlist doctrine as the domain, applied to the ONLY local-part
 *   character this function ever emits — which makes the whole function
 *   allowlist-shaped. Why it had to be: the control strip above is a DENYLIST,
 *   an enumerated RANGE, so a class nobody enumerated walks straight through it.
 *   `U+202E` RIGHT-TO-LEFT OVERRIDE is Unicode category **Cf** — not C0, not
 *   whitespace, not a line terminator — so it survived both the strip and
 *   `.trim()` and landed as the FIRST character emitted, reversing the rest of
 *   that log line in any bidi-aware viewer. `U+200F` and `U+2066`–`U+2069` are
 *   the same family, and the next such class is the one this clause exists to
 *   survive: an allowlist does not need to have heard of it. The failure answer
 *   here is deliberately NOT `'(redacted)'` — a disallowed initial is simply
 *   DROPPED, giving `***@domain`. The initial is a HINT, never an identifier, so
 *   losing it costs nothing an operator needed, whereas failing the whole value
 *   closed would turn every ordinary non-ASCII local part (`émile@example.com`)
 *   into a useless log line. Three more classes fall to the same clause for
 *   free: the spreadsheet-formula initials `=` `+` `-` `@` that {@link csvCell}
 *   guards against in a log exported to CSV; a lone surrogate, which is what
 *   `slice(0, 1)` yields for an astral first character; and any control
 *   character, if the strip above is ever weakened.
 * - **Domain truncated to 60 chars.** A log line is a bounded resource; an
 *   overlong value must not push real entries out of a tail buffer.
 *
 * The output is for HUMANS reading logs — it is deliberately lossy and MUST NOT
 * be parsed back or used as an identifier.
 */
export function redactEmailForLog(value: unknown): string {
  if (typeof value !== 'string') return REDACTED;
  // Log-line forgery AND terminal-control injection first, before any parsing
  // decision reads the value. The whole C0 range + DEL, not just CR/LF/TAB: the
  // rule exists to catch control characters that landed in a pattern by
  // ACCIDENT; here they are the deliberate subject.
  // eslint-disable-next-line no-control-regex
  const cleaned = value.replace(/[\x00-\x1F\x7F]/g, '').trim();
  if (cleaned === '') return REDACTED;

  const at = cleaned.lastIndexOf('@');
  if (at === -1) return REDACTED; // not email-shaped → never echoed back
  const local = cleaned.slice(0, at);
  const domain = cleaned.slice(at + 1); // the FULL domain side — see below
  if (domain === '') return REDACTED; // `user@` tells us nothing and leaks the local part
  // Character ALLOWLIST, not a domain grammar. Validate the WHOLE side (the
  // untruncated `cleaned.slice(at + 1)` captured above), THEN truncate:
  // validating `domain.slice(0, 60)` would let 60 clean characters launder
  // whatever followed them into the mask. Negated class, not an anchored
  // `/^[A-Za-z0-9.-]+$/` — a clarity preference, NOT because an anchor would be
  // unsafe (SAFE_INITIAL's docblock retracts that claim about JS `$`).
  if (/[^A-Za-z0-9.-]/.test(domain)) return REDACTED;
  const shown = domain.slice(0, MAX_DOMAIN_LENGTH); // truncate only for emission

  // The other allowlist: the initial is the only local-part character emitted,
  // and a denylist there let `U+202E` (category Cf) through to reverse the line.
  // `.slice(0, 1)` rather than `local[0]` — always a string, no index assertion.
  const initial = local.slice(0, 1);
  const showInitial = local.length >= 3 && SAFE_INITIAL.test(initial);
  return showInitial ? `${initial}***@${shown}` : `***@${shown}`;
}

// The message-body scrubber's two patterns. Consolidated here from
// cca-parishes-worker (bead cca-nip4) so a SECOND sign-in door — CCA-decides —
// could reuse the ONE audited scrubber rather than a deep import or a copy
// (ARCH-11). Behaviour is byte-for-byte what parishes shipped.
const EMAIL_RE = /([A-Za-z0-9._%+-])[A-Za-z0-9._%+-]*(@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
const PHONE_RE = /\+?\d[\d\s\-().]{6,}\d/g;

/**
 * Scrub PII out of a **message BODY of unknown shape** — a provider error
 * string, an SSO log line, the registrar's OTP-failure alert email — masking
 * any address or phone number found INSIDE it: an email is rewritten to
 * `x***@domain` (the first character kept as a hint, as {@link redactEmailForLog}
 * emits) and a phone-shaped digit run to `[redacted-phone]`.
 *
 * THE VALUE-MASKER / TEXT-SCRUBBER SPLIT (SEC-5), stated because the two now sit
 * side by side and fail in OPPOSITE directions on purpose:
 *   - {@link redactEmailForLog} takes ONE value that ought to be an address and
 *     must NEVER echo it — anything it cannot parse fails CLOSED to `(redacted)`.
 *   - `redactPII` takes a whole message and must survive intact when there is
 *     nothing to redact, so `String.replace`'s no-match pass-through is the
 *     CORRECT behaviour here (blanking a diagnostic estate-wide would destroy an
 *     operator's only evidence) — and a DEFECT in a value slot. It is control-
 *     blind and returns its input verbatim on no-match, so it must NEVER be used
 *     to mask a lone attacker-chosen value; reach for `redactEmailForLog` there.
 *
 * Moved verbatim from cca-parishes-worker (bead cca-nip4); the exact behaviour is
 * pinned by that package's `test/redact.test.ts`. The output is for HUMANS
 * reading logs — deliberately lossy, and never to be parsed back as an identifier.
 */
export function redactPII(value: string): string {
  return value.replace(EMAIL_RE, '$1***$2').replace(PHONE_RE, '[redacted-phone]');
}

// The plain-text-email link defanger's three patterns. Each is linear: the
// first is a fixed three characters; in the second every repetition starts with
// a literal `.` a digit run cannot hold, so it cannot be split two ways; the
// third is one character of context either side. A 64 KiB body costs one pass.
// No lookbehind: this package has browser consumers.
/** Every `://`, whatever precedes it — a scheme of any length or shape, or none. */
const COLON_SLASHES_RE = /:\/\//g;
/** A run of FOUR OR MORE groups of digits, each of any width, joined by single
 *  dots and starting at a word boundary — an IPv4 dotted quad, and anything that
 *  holds one. */
const DOTTED_DIGITS_RE = /\b\d+(?:\.\d+){3,}/g;
/** A dot with a non-space character before it and, after it, a character that
 *  is Unicode-Alphabetic or a combining mark (U+FE0F, U+034F must not hide what
 *  follows them). */
const HOST_DOT_RE = /([^\s.])\.(?=[\p{Alphabetic}\p{M}])/gu;

/**
 * Make the links in a **plain-text body bound for an operator's mailbox** inert
 * — the sink of a third party's words copied into an email the registrar reads
 * (the 6am heartbeat's problem reports, cca-rh4qy). A mail client turns
 * `https://evil.example.com`, a bare `evil.example.com` or `192.0.2.1/login` into
 * links on its own; after this none is one. Three rules, in this order:
 *   - **every `://`** becomes `[:]//`, whatever precedes it (`https[:]//`,
 *     `smb[:]//`, `FTP[:]//`, a scheme of any length, or none at all);
 *   - **a run of four or more dot-joined digit groups** — groups of any width,
 *     starting at a word boundary, so every IPv4 dotted quad and anything that
 *     holds one — loses EVERY dot (`192[.]0[.]2[.]1`, `1[.]2[.]3[.]4[.]5`), so no
 *     later cut can leave a live quad behind;
 *   - **a host's dots** — a dot with a non-space character before it and,
 *     after it, a character with Unicode's derived Alphabetic property (the
 *     letters of every script, letter numbers such as the Roman numerals
 *     U+2160–217F, and the Other_Alphabetic symbols such as the circled Latin
 *     U+24B6–24E9 and squared Latin U+1F130–1F149) or a combining mark, becomes
 *     `[.]`. A host name's last dot sits before its top-level label, which
 *     starts with such a character, so no host name of that kind survives with
 *     a live dot (`evil[.]example[.]com`).
 *
 * This is the plain-text-email-to-an-operator sink (Lazo's HOUSE note on
 * cca-rh4qy); bead cca-m5ew plans to move the parishes worker's `scrubMessage`
 * here beside it. Run it AFTER {@link redactPII} — the email pattern needs a
 * live dot to find an address — and BEFORE any length cut: it lengthens its
 * input (each `.` it defangs becomes three characters, each `://` five), and a
 * cut can never put a dot or a `://` back.
 *
 * WHAT IT OVER-DEFANGS, by design and at no cost an operator pays: any
 * `word.Word` with no space after the dot — a file name (`report[.]pdf`), `e[.]g.`,
 * a sentence run on after its full stop — and every dot of a dotted number of
 * four or more groups, of any width (`1.2.3.1234` → `1[.]2[.]3[.]1234`). The
 * boundary against numbers is exact: three groups or fewer are left alone
 * (`3.14`, `v1.2`, `1.2.3`), as is a run that does not start at a word boundary
 * (`v1.2.3.4`).
 *
 * WHAT IT DOES NOT CATCH: a host whose dots are not U+002E (the ideographic and
 * fullwidth full stops some IDN parsers accept); a host whose top-level label
 * starts with a character outside that class; a dotless host written with no
 * scheme (`localhost:8080`, `intranet/login`); an IPv6 literal, or an IPv4
 * address in any form but dotted digit groups (a bare integer, hex), written
 * with no scheme; and a scheme written without `//` (`https:host`,
 * `https:\\host`), whose host is then dead only if its own dots are. It makes
 * text inert for a plain-text reader — it is not an HTML escaper
 * ({@link escapeHtml} is) and does not touch line breaks (the caller's scrubber
 * does). Pure and idempotent: defanged text has no `://`, no run of four
 * dot-joined digit groups and no defangable dot left.
 */
export function defangLinks(text: string): string {
  return text
    .replace(COLON_SLASHES_RE, '[:]//')
    .replace(DOTTED_DIGITS_RE, (run) => run.split('.').join('[.]'))
    .replace(HOST_DOT_RE, '$1[.]');
}

/** An Error `name` that {@link errorTypeAndStatus} will log verbatim: an
 *  identifier, 1–64 characters, ASCII only (no `u` flag, so `\w` is
 *  `[A-Za-z0-9_]`). No `g`/`y` flag — `.test` must stay stateless. */
const ERROR_TYPE_NAME = /^[A-Za-z_$][\w$]{0,63}$/;

/**
 * Reduce a CAUGHT value to the two facts a log line may carry about it: its
 * TYPE and its HTTP `status`. Built for the error field of a sign-in log line
 * (bead cca-1s4u, SEC-5 / OBS-5), where the doors used to log `String(err)` —
 * and a provider puts its response body in the message: Resend's 422 body
 * echoes the recipient address in full, so a failed code email wrote the
 * address the masked `email` field beside it had just withheld.
 *
 * - **`type`** — an `Error`'s `name` when it is identifier-shaped
 *   (`TypeError`, `ResendError`), else the literal `'Error'`: a name is chosen
 *   by whoever constructed the error, so an address, a space or a 65th
 *   character is not echoed. A non-string `name` (a Symbol, a number) and any
 *   value that is not an `Error` at all are `'Unknown'`.
 * - **`status`** — kept only when `typeof` the value is `'object'` (and it is not
 *   `null`) and its own or inherited `status` is an INTEGER from 100 to 599;
 *   anything else (`'422'`, `422.5`, `NaN`, `600`) is `null`. A FUNCTION does not
 *   qualify, even though `'status' in fn` would accept one: a thrown function
 *   carrying `status: 503` gives `{ type: 'Unknown', status: null }`. A plain
 *   object with a valid `status` gives `{ type: 'Unknown', status }`.
 *
 * What it NEVER does, each for a reason: it never reads `message`, `stack` or
 * `cause` (each can carry the address or a provider body); it never calls
 * `String()` or a `toString`/`valueOf` on the value (author code that can
 * leak or throw); and it never THROWS — the whole body is in a try/catch that
 * answers `{ type: 'Unknown', status: null }`, because every caller runs it
 * inside a `catch` block, where a throw would turn a handled 502 into an
 * unhandled 500, or break the byte-identical 200 of the OTP issuance gate. A
 * hostile Proxy, a throwing getter and a null-prototype object are all just
 * `'Unknown'`.
 *
 * It CHOOSES fields; it does not transform text, so it is neither a masker nor
 * a scrubber (no `String.replace`, and deliberately not named like one).
 */
export function errorTypeAndStatus(err: unknown): { type: string; status: number | null } {
  try {
    let type = 'Unknown';
    if (err instanceof Error) {
      const name: unknown = err.name;
      if (typeof name === 'string') type = ERROR_TYPE_NAME.test(name) ? name : 'Error';
    }
    let status: number | null = null;
    if (typeof err === 'object' && err !== null && 'status' in err) {
      const value: unknown = err.status;
      if (typeof value === 'number' && Number.isInteger(value) && value >= 100 && value <= 599) {
        status = value;
      }
    }
    return { type, status };
  } catch {
    return { type: 'Unknown', status: null };
  }
}

/**
 * Constant-time string equality for secret / token comparisons (webhook bearer
 * secrets, invitation tokens, Drive channel tokens). The length difference is
 * folded INTO the accumulator rather than triggering an early return, and the
 * character loop never short-circuits on the first mismatch — so the comparison
 * time reveals neither how much of `b` matched nor (beyond the fixed loop) its
 * length. Consolidates the three drifted local copies (vetting webhook,
 * health-assessment db, xero events route) into one audited implementation.
 * Order-independent for equality; pass presented-vs-expected in either slot.
 */
export function timingSafeEqual(a: string, b: string): boolean {
  let diff = a.length ^ b.length;
  for (let i = 0; i < b.length; i++) {
    // charCodeAt past the end of `a` is NaN; `| 0` coerces it to 0 for the XOR.
    diff |= (a.charCodeAt(i) | 0) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

/**
 * Constant-time `Authorization: Bearer <token>` check for a shared-secret gate
 * (the estate /api/summary surfaces). Compares SHA-256 digests of the presented
 * token and the configured secret — equal-length buffers XOR-folded without
 * early exit, so a byte-wise comparison can't leak a prefix match through
 * timing. Fail closed (SEC-6): no secret configured → false; EMPTY secret
 * (misdeployed) → false; a non-`Bearer ` scheme (case-SENSITIVE, per RFC 6750
 * as this gate's sole caller is our own dashboard) → false; an empty or
 * whitespace-only token → false. The token is trimmed after the scheme is
 * stripped; the secret is compared verbatim.
 *
 * Single-homes the four byte-identical `summaryBearerOk` copies that grew on
 * the parishes/corpus/xero workers + the forms Pages Function (2026-07-21,
 * Gate-2 cca-bd7z disposition) — each call site keeps only its thin 401
 * wrapper. Requires WebCrypto (`crypto.subtle`) — present in all four target
 * runtimes and in Node ≥ 20.
 */
export async function bearerTokenMatches(
  authorizationHeader: string | null | undefined,
  secret: string | undefined,
): Promise<boolean> {
  if (!secret) return false;
  const token = authorizationHeader?.startsWith('Bearer ')
    ? authorizationHeader.slice(7).trim()
    : '';
  if (!token) return false;
  const enc = new TextEncoder();
  const [a, b] = await Promise.all([
    crypto.subtle.digest('SHA-256', enc.encode(token)),
    crypto.subtle.digest('SHA-256', enc.encode(secret)),
  ]);
  const av = new Uint8Array(a);
  const bv = new Uint8Array(b);
  let diff = 0;
  for (let i = 0; i < av.length; i++) diff |= (av[i] ?? 0) ^ (bv[i] ?? 0);
  return diff === 0;
}
